"use client";

import React from "react";

export interface TherapistCardProps {
  id: string;
  firstName: string;
  lastName: string;
  credential: string;
  profileImage: string;
  therapistTier?: "resident" | "associate";
  acceptingClients: boolean;
  waitlistAvailable?: boolean;
  acceptingFor?: string[];
  onClick?: () => void;
}

export function TherapistCard({
  id,
  firstName,
  lastName,
  credential,
  profileImage,
  therapistTier,
  acceptingClients,
  waitlistAvailable,
  acceptingFor,
  onClick
}: TherapistCardProps) {
  return (
    <div
      className="bg-white rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer group border border-gray-100 w-[260px]"
      onClick={onClick || (() => window.location.href = `/therapist/${id}`)}
    >
      {/* Profile Photo - Fixed dimensions */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        {profileImage ? (
          <img
            src={profileImage}
            alt={`${firstName} ${lastName}`}
            className="w-[260px] h-auto object-cover"
          />
        ) : (
          <div className="w-[260px] h-[330px] bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
            <span className="text-6xl font-semibold text-primary-600">
              {firstName.charAt(0)}{lastName.charAt(0)}
            </span>
          </div>
        )}
        {/* Status pill on bottom right */}
        <div className="absolute bottom-3 right-3">
          {acceptingClients ? (
            <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-medium px-3 py-1.5 rounded-full">
              {acceptingFor?.length === 2
                ? 'Accepting individual & couples'
                : acceptingFor?.length === 1
                ? `Accepting ${acceptingFor[0]!.toLowerCase()} only`
                : 'Accepting new clients'}
            </span>
          ) : waitlistAvailable ? (
            <span className="bg-yellow-100 text-yellow-800 border border-yellow-200 text-xs font-medium px-3 py-1.5 rounded-full">
              Waitlist available
            </span>
          ) : (
            <span className="bg-gray-100 text-gray-600 border border-gray-200 text-xs font-medium px-3 py-1.5 rounded-full">
              Not accepting
            </span>
          )}
        </div>
      </div>
      
      {/* Info */}
      <div className="p-5">
        <h3 className="font-semibold text-gray-900 text-base leading-tight group-hover:text-primary-600 transition-colors">
          {firstName} {lastName}
        </h3>
        <p className="text-sm text-gray-600 mt-1">{credential}</p>
        {therapistTier && (
          <div className="mt-3">
            <span className="text-xs text-gray-500 capitalize">
              {therapistTier === 'resident' ? 'Intern Therapist' : 'Associate Therapist'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}